import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requirePreferences?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = false,
  requirePreferences = false 
}) => {
  const { user, isAuthenticated, isLoading, anonymousSessionId } = useAuth();
  const { preferences } = useData();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    // Don't redirect while still loading
    if (isLoading) return;

    // Check authentication requirement
    if (requireAuth && !isAuthenticated) {
      setRedirectPath('/auth');
      setShouldRedirect(true);
      return;
    }

    // Check preferences requirement (for both authenticated and anonymous users)
    if (requirePreferences) {
      // For authenticated users, check if they have preferences
      if (isAuthenticated && user) {
        if (!preferences.data || Object.keys(preferences.data).length === 0) {
          setRedirectPath('/welcome');
          setShouldRedirect(true);
          return;
        }
      }
      // For anonymous users, they can always access preferences (no redirect needed)
      // The preferences page will handle showing the form for anonymous users
    }

    setShouldRedirect(false);
  }, [isLoading, requireAuth, requirePreferences, isAuthenticated, user, anonymousSessionId, preferences.data]);

  // Show loading state while checking
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect if needed
  if (shouldRedirect && redirectPath) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Render children if all checks pass
  return <>{children}</>;
};
